import React, { useState, useCallback } from "react";
import {
  Table,
  Button,
  Space,
  Input,
  Select,
  Card,
  Typography,
  Tag,
  Modal,
  Form,
  Row,
  Col,
  DatePicker,
  Statistic,
  Divider,
  InputNumber,
  Switch,
  Tooltip,
  Image,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  EyeOutlined,
  GiftOutlined,
  FireOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import moment from "moment";

// ✅ Import Custom Hooks
import {
  usePromotions,
  usePromotionStats,
  useCreatePromotion,
  useUpdatePromotion,
  useDeletePromotion,
  useTogglePromotionStatus,
  useIsPromotionCurrent,
} from "../hooks/Usepromotions";

// ✅ Import Types
import type { Promotion } from "../services/promotionservice";
import UploadImageOne from "../components/UploadImageOne";
import { consts } from "../utils";

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

// =====================================================
// 🎨 Types
// =====================================================

type BadgeType = "HOT DEAL" | "SPECIAL" | "LIMITED" | "NEW" | "SALE";

interface SearchParams {
  page: number;
  limit: number;
  search: string;
  isActive?: boolean;
  badge?: BadgeType;
}

// =====================================================
// 🎨 Main Component
// =====================================================

const PromotionsPage: React.FC = () => {
  // States
  const [searchParams, setSearchParams] = useState<SearchParams>({
    page: 1,
    limit: 10,
    search: "",
    isActive: undefined,
    badge: undefined,
  });

  const [selectedPromotion, setSelectedPromotion] = useState<Promotion | null>(
    null
  );
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [form] = Form.useForm();

  // =====================================================
  // ✅ Use Custom Hooks
  // =====================================================
  const { imageName, setImageName, UploadImage } = UploadImageOne();

  // Queries
  const {
    data: promotionsResponse,
    isLoading,
    refetch,
  } = usePromotions(searchParams);

  const { data: statsData } = usePromotionStats();

  // Mutations
  const createMutation = useCreatePromotion();
  const updateMutation = useUpdatePromotion();
  const deleteMutation = useDeletePromotion();
  const toggleStatusMutation = useTogglePromotionStatus();

  // Helper Hook
  const isPromotionCurrent = useIsPromotionCurrent;

  // =====================================================
  // Helper Functions
  // =====================================================

  const openModal = useCallback(
    (type: string, promotion?: Promotion) => {
      setActiveModal(type);
      if (promotion) {
        setSelectedPromotion(promotion);
        if (type === "edit") {
          setImageName(promotion?.image);
          form.setFieldsValue({
            ...promotion,
            dateRange: [moment(promotion.startDate), moment(promotion.endDate)],
          });
        }
      }
    },
    [form]
  );

  const closeModal = useCallback(() => {
    setActiveModal(null);
    setSelectedPromotion(null);
    form.resetFields();
  }, [form]);

  const handleSubmit = useCallback(
    (values: any) => {
      const data = {
        ...values,
        image: imageName,
        startDate: values.dateRange[0].toISOString(),
        endDate: values.dateRange[1].toISOString(),
      };
      delete data.dateRange;

      if (selectedPromotion) {
        updateMutation.mutate(
          { id: selectedPromotion._id, data },
          {
            onSuccess: () => {
              closeModal();
            },
          }
        );
      } else {
        createMutation.mutate(data, {
          onSuccess: () => {
            closeModal();
          },
        });
      }
    },
    [selectedPromotion, createMutation, updateMutation, closeModal]
  );

  const handleDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        title: "ຢືນຢັນການລຶບ",
        content: "ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການລຶບໂປຣໂມຊັ່ນນີ້?",
        okText: "ລຶບ",
        cancelText: "ຍົກເລີກ",
        okButtonProps: { danger: true },
        onOk: () => deleteMutation.mutate(id),
      });
    },
    [deleteMutation]
  );

  // Get Badge Color
  const getBadgeColor = (badge: string): string => {
    const colors: Record<string, string> = {
      "HOT DEAL": "red",
      SPECIAL: "gold",
      LIMITED: "orange",
      NEW: "green",
      SALE: "magenta",
    };
    return colors[badge] || "blue";
  };

  // =====================================================
  // Table Columns
  // =====================================================

  const columns = [
    {
      title: "🎁 ໂປຣໂມຊັ່ນ",
      dataIndex: "image",
      key: "image",
      render: (image: string) => (
        <div>
          <Image style={{height:100}} src={image ? consts.URL_PHOTO_AW3 + image : ""} />
        </div>
      ),
    },
    {
      title: "🎁 ໂປຣໂມຊັ່ນ",
      dataIndex: "title",
      key: "title",
      render: (title: string, record: Promotion) => (
        <div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{title}</div>
          <Tag color={getBadgeColor(record.badge)}>{record.badge}</Tag>
        </div>
      ),
    },
    {
      title: "💰 ສ່ວນຫຼຸດ",
      key: "discount",
      width: 120,
      render: (_: any, record: Promotion) => (
        <div style={{ fontWeight: 600, fontSize: 16, color: "#f56a00" }}>
          {record.discountType === "percentage"
            ? `-${record.discountPercentage}%`
            : `${record.discountAmount.LAK.toLocaleString()} ₭`}
        </div>
      ),
    },
    {
      title: "📅 ວັນທີ",
      key: "date",
      width: 200,
      render: (_: any, record: Promotion) => (
        <div>
          <div>{moment(record.startDate).format("DD/MM/YYYY")}</div>
          <div style={{ fontSize: 12, color: "#666" }}>
            ຫາ {moment(record.endDate).format("DD/MM/YYYY")}
          </div>
        </div>
      ),
    },

    {
      title: "✅ ສະຖານະ",
      key: "status",
      width: 120,
      render: (_: any, record: Promotion) => (
        <div>
          <Switch
            checked={record.isActive}
            onChange={() => toggleStatusMutation.mutate(record._id)}
            loading={toggleStatusMutation.isLoading}
            checkedChildren="ເປີດ"
            unCheckedChildren="ປິດ"
          />
          <div style={{ marginTop: 4 }}>
            {isPromotionCurrent(record) ? (
              <Tag color="green">🔥 ກຳລັງໃຊ້</Tag>
            ) : moment(record.endDate).isBefore(moment()) ? (
              <Tag color="red">⏰ ໝົດອາຍຸ</Tag>
            ) : (
              <Tag color="blue">⏳ ຍັງບໍ່ເລີ່ມ</Tag>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "⚙️ ຈັດການ",
      key: "actions",
      width: 120,
      render: (_: any, record: Promotion) => (
        <Space>
          <Tooltip title="ເບິ່ງລາຍລະອຽດ">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => openModal("view", record)}
            />
          </Tooltip>
          <Tooltip title="ແກ້ໄຂ">
            <Button
              size="small"
              type="primary"
              icon={<EditOutlined />}
              onClick={() => openModal("edit", record)}
            />
          </Tooltip>
          <Tooltip title="ລຶບ">
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record._id)}
              loading={deleteMutation.isLoading}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // =====================================================
  // Render
  // =====================================================

  const promotions = promotionsResponse?.data?.promotions || [];
  const pagination = promotionsResponse?.data?.pagination;

  return (
    <div style={{ padding: 10 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <Title level={2} style={{ margin: 0, color: "#1890ff" }}>
              🎁 ຈັດການໂປຣໂມຊັ່ນ
            </Title>
            <Text type="secondary">ສ້າງ ແລະ ຈັດການໂປຣໂມຊັ່ນພິເສດ</Text>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()} />
            <Button
              type="primary"
              size="large"
              icon={<PlusOutlined />}
              onClick={() => openModal("create")}
              style={{ borderRadius: 8 }}
            >
              ສ້າງໂປຣໂມຊັ່ນໃໝ່
            </Button>
          </Space>
        </div>

        {/* Statistics */}
        {statsData?.data && (
          <Row gutter={16} style={{ marginTop: 24 }}>
            <Col xs={24} sm={6}>
              <Card>
                <Statistic
                  title="📋 ທັງໝົດ"
                  value={statsData.data.totalPromotions}
                  prefix={<GiftOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={6}>
              <Card>
                <Statistic
                  title="🔥 ກຳລັງໃຊ້"
                  value={statsData.data.activePromotions}
                  valueStyle={{ color: "#52c41a" }}
                  prefix={<FireOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={6}>
              <Card>
                <Statistic
                  title="⏰ ໝົດອາຍຸ"
                  value={statsData.data.expiredPromotions}
                  valueStyle={{ color: "#ff4d4f" }}
                  prefix={<CloseCircleOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={6}>
              <Card>
                <Statistic
                  title="⏳ ຈະມາເຖິງ"
                  value={statsData.data.upcomingPromotions}
                  prefix={<CheckCircleOutlined />}
                />
              </Card>
            </Col>
          </Row>
        )}
      </div>

      {/* Filters */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Input
              placeholder="🔍 ຄົ້ນຫາໂປຣໂມຊັ່ນ..."
              allowClear
              onChange={(e) =>
                setSearchParams((prev) => ({
                  ...prev,
                  search: e.target.value,
                  page: 1,
                }))
              }
            />
          </Col>
          <Col xs={24} md={8}>
            <Select
              placeholder="📊 ສະຖານະ"
              allowClear
              style={{ width: "100%" }}
              onChange={(value: string | undefined) =>
                setSearchParams((prev) => ({
                  ...prev,
                  isActive:
                    value === "true"
                      ? true
                      : value === "false"
                      ? false
                      : undefined,
                  page: 1,
                }))
              }
            >
              <Option value="true">ເປີດໃຊ້ງານ</Option>
              <Option value="false">ປິດໃຊ້ງານ</Option>
            </Select>
          </Col>
          <Col xs={24} md={8}>
            <Select
              placeholder="🏷️ Badge"
              allowClear
              style={{ width: "100%" }}
              onChange={(value: BadgeType | undefined) =>
                setSearchParams((prev) => ({ ...prev, badge: value, page: 1 }))
              }
            >
              <Option value="HOT DEAL">HOT DEAL</Option>
              <Option value="SPECIAL">SPECIAL</Option>
              <Option value="LIMITED">LIMITED</Option>
              <Option value="NEW">NEW</Option>
              <Option value="SALE">SALE</Option>
            </Select>
          </Col>
        </Row>
      </Card>

      {/* Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={promotions}
          rowKey="_id"
          loading={isLoading}
          pagination={{
            current: pagination?.current || 1,
            pageSize: searchParams.limit || 10,
            total: pagination?.total || 0,
            showSizeChanger: true,
            showTotal: (total) => `ທັງໝົດ ${total} ລາຍການ`,
            onChange: (page, pageSize) =>
              setSearchParams((prev) => ({
                ...prev,
                page,
                limit: pageSize || 10,
              })),
          }}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={selectedPromotion ? "✏️ ແກ້ໄຂໂປຣໂມຊັ່ນ" : "➕ ສ້າງໂປຣໂມຊັ່ນໃໝ່"}
        open={activeModal === "create" || activeModal === "edit"}
        onCancel={closeModal}
        footer={null}
        width={800}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={24} style={{ margin: "20px 0px" }}>
              {UploadImage()}
            </Col>

            <Col span={12}>
              <Form.Item
                name="title"
                label="ຊື່ໂປຣໂມຊັ່ນ"
                rules={[{ required: true, message: "ກະລຸນາປ້ອນຊື່ໂປຣໂມຊັ່ນ" }]}
              >
                <Input placeholder="ເຊັ່ນ: ສັບປະດາ Refresh" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="badge"
                label="Badge"
                rules={[{ required: true, message: "ກະລຸນາເລືອກ Badge" }]}
              >
                <Select placeholder="ເລືອກ Badge">
                  <Option value="HOT DEAL">HOT DEAL</Option>
                  <Option value="SPECIAL">SPECIAL</Option>
                  <Option value="LIMITED">LIMITED</Option>
                  <Option value="NEW">NEW</Option>
                  <Option value="SALE">SALE</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="ລາຍລະອຽດ"
            rules={[{ required: true, message: "ກະລຸນາປ້ອນລາຍລະອຽດ" }]}
          >
            <TextArea rows={3} placeholder="ອະທິບາຍໂປຣໂມຊັ່ນ..." />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="discountType"
                label="ປະເພດສ່ວນຫຼຸດ"
                rules={[
                  { required: true, message: "ກະລຸນາເລືອກປະເພດສ່ວນຫຼຸດ" },
                ]}
                initialValue="percentage"
              >
                <Select>
                  <Option value="percentage">ສ່ວນຫຼຸດ %</Option>
                  <Option value="fixed">ຈຳນວນເງິນ</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                noStyle
                shouldUpdate={(prev, curr) =>
                  prev.discountType !== curr.discountType
                }
              >
                {({ getFieldValue }) =>
                  getFieldValue("discountType") === "percentage" ? (
                    <Form.Item
                      name="discountPercentage"
                      label="ສ່ວນຫຼຸດ (%)"
                      rules={[
                        { required: true, message: "ກະລຸນາປ້ອນສ່ວນຫຼຸດ" },
                      ]}
                    >
                      <InputNumber
                        min={0}
                        max={100}
                        style={{ width: "100%" }}
                        addonAfter="%"
                        placeholder="0-100"
                      />
                    </Form.Item>
                  ) : (
                    <Form.Item
                      name={["discountAmount", "LAK"]}
                      label="ສ່ວນຫຼຸດ (LAK)"
                      rules={[
                        { required: true, message: "ກະລຸນາປ້ອນຈຳນວນເງິນ" },
                      ]}
                    >
                      <InputNumber
                        min={0}
                        style={{ width: "100%" }}
                        addonAfter="₭"
                        placeholder="0"
                      />
                    </Form.Item>
                  )
                }
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="maxUsage" label="ຈຳນວນຈຳກັດ">
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  placeholder="ບໍ່ຈຳກັດ"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="dateRange"
            label="ໄລຍະເວລາ"
            rules={[{ required: true, message: "ກະລຸນາເລືອກໄລຍະເວລາ" }]}
          >
            <RangePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
          </Form.Item>

          <Form.Item
            name="applicableTo"
            label="ໃຊ້ໄດ້ກັບ"
            initialValue="all"
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="all">ທັງໝົດ</Option>
              <Option value="specific_courses">ຄອສສະເພາະ</Option>
              <Option value="specific_categories">ໝວດໝູ່ສະເພາະ</Option>
            </Select>
          </Form.Item>

          <Form.Item name="terms" label="ເງື່ອນໄຂ">
            <TextArea rows={2} placeholder="ເງື່ອນໄຂການໃຊ້ງານ..." />
          </Form.Item>

          <Divider />

          <Form.Item>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button onClick={closeModal}>ຍົກເລີກ</Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={createMutation.isLoading || updateMutation.isLoading}
              >
                {selectedPromotion ? "ອັບເດດ" : "ສ້າງ"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* View Modal */}
      <Modal
        title="📋 ລາຍລະອຽດໂປຣໂມຊັ່ນ"
        open={activeModal === "view"}
        onCancel={closeModal}
        footer={[
          <Button key="close" onClick={closeModal}>
            ປິດ
          </Button>,
        ]}
        width={600}
      >
        {selectedPromotion && (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <div style={{ marginBottom: 16 }}>
                  <Tag
                    color={getBadgeColor(selectedPromotion.badge)}
                    style={{ fontSize: 14 }}
                  >
                    {selectedPromotion.badge}
                  </Tag>
                </div>
                <Title level={3} style={{ marginTop: 0 }}>
                  {selectedPromotion.title}
                </Title>
                <Text type="secondary">{selectedPromotion.description}</Text>
              </Col>

              <Col span={12}>
                <Text strong>ສ່ວນຫຼຸດ:</Text>
                <div
                  style={{ fontSize: 24, fontWeight: 700, color: "#f56a00" }}
                >
                  {selectedPromotion.discountType === "percentage"
                    ? `-${selectedPromotion.discountPercentage}%`
                    : `${selectedPromotion.discountAmount.LAK.toLocaleString()} ₭`}
                </div>
              </Col>

              <Col span={12}>
                <Text strong>ການໃຊ້ງານ:</Text>
                <div style={{ fontSize: 20 }}>
                  {selectedPromotion.currentUsage}/
                  {selectedPromotion.maxUsage || "∞"}
                </div>
              </Col>

              <Col span={12}>
                <Text strong>ວັນທີເລີ່ມ:</Text>
                <div>
                  {moment(selectedPromotion.startDate).format("DD/MM/YYYY")}
                </div>
              </Col>

              <Col span={12}>
                <Text strong>ວັນທີສິ້ນສຸດ:</Text>
                <div>
                  {moment(selectedPromotion.endDate).format("DD/MM/YYYY")}
                </div>
              </Col>

              <Col span={24}>
                <Text strong>ສະຖານະ:</Text>
                <div style={{ marginTop: 8 }}>
                  {isPromotionCurrent(selectedPromotion) ? (
                    <Tag color="green" style={{ fontSize: 14 }}>
                      🔥 ກຳລັງໃຊ້ງານ
                    </Tag>
                  ) : moment(selectedPromotion.endDate).isBefore(moment()) ? (
                    <Tag color="red" style={{ fontSize: 14 }}>
                      ⏰ ໝົດອາຍຸແລ້ວ
                    </Tag>
                  ) : (
                    <Tag color="blue" style={{ fontSize: 14 }}>
                      ⏳ ຍັງບໍ່ເລີ່ມຕົ້ນ
                    </Tag>
                  )}
                </div>
              </Col>

              {selectedPromotion.terms && (
                <Col span={24}>
                  <Text strong>ເງື່ອນໄຂ:</Text>
                  <div
                    style={{
                      marginTop: 8,
                      padding: 12,
                      background: "#f5f5f5",
                      borderRadius: 4,
                    }}
                  >
                    {selectedPromotion.terms}
                  </div>
                </Col>
              )}
            </Row>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PromotionsPage;
